import React, { useEffect, useRef } from "react"
import { CARDS_FLIPPED_EVENT } from "../util/util"

const leafSources = [
    require("../assets/backgrounds/leaf.png") as string,
    require("../assets/backgrounds/leaf2.png") as string,
    require("../assets/backgrounds/leaf3.png") as string,
]

interface Leaf {
    x: number
    y: number
    vx: number
    vy: number
    rotation: number
    spin: number
    flutter: number
    flutterSpeed: number
    size: number
    image: number
    depth: number
    roll: number
    rollSpeed: number
    pitch: number
}

interface WindRibbon {
    originX: number
    originY: number
    dir: number
    speed: number
    t: number
    duration: number
    waveK: number
    waveK2: number
    amp: number
    amp2: number
    loopAt: number
    loopDur: number
    loopRadius: number
    loopCenterX: number
    loopCenterY: number
    startA: number
    fadeWindow: number
    trail: { x: number; y: number }[]
}

function rand(min: number, max: number) {
    return min + Math.random() * (max - min)
}

function loadImages(): Promise<HTMLImageElement[]> {
    return Promise.all(leafSources.map(src => new Promise<HTMLImageElement>((resolve, reject) => {
        const img = new Image()
        img.onload = () => resolve(img)
        img.onerror = reject
        img.src = src
    })))
}

export default function FallingLeavesBackground() {
    const canvasRef = useRef<HTMLCanvasElement>(null)

    useEffect(() => {
        const canvas = canvasRef.current
        if (!canvas) {
            return
        }
        const ctx = canvas.getContext("2d", { alpha: true, desynchronized: true })
        if (!ctx) {
            return
        }

        let width = 0
        let height = 0
        let raf = 0
        let running = true
        let last = performance.now()
        let wind = 6
        let targetWind = 8
        let gustTimer = 1.2
        let pendingSpawns = 0
        let spawnWait = 0
        let burstSpawns = false
        let burstLanes: number[] = []
        let sortTick = 0
        const leaves: Leaf[] = []
        const ribbons: WindRibbon[] = []
        let images: HTMLImageElement[] = []

        function resize() {
            width = window.innerWidth
            height = window.innerHeight
            const dpr = Math.min(window.devicePixelRatio || 1, 1.5)
            const cap = Math.min(1, 1920 / Math.max(1, width), 1080 / Math.max(1, height))
            const scale = dpr * cap
            canvas!.width = Math.max(1, Math.floor(width * scale))
            canvas!.height = Math.max(1, Math.floor(height * scale))
            canvas!.style.width = `${width}px`
            canvas!.style.height = `${height}px`
            ctx!.setTransform(scale, 0, 0, scale, 0, 0)
            ctx!.imageSmoothingEnabled = cap < 0.999
        }

        function spawnLeaf(anywhere: boolean): Leaf {
            const depth = Math.pow(Math.random(), 0.65)
            return {
                x: rand(-40, width + 40),
                y: anywhere ? rand(-40, height) : rand(-140, -24),
                vx: rand(-4, 4),
                vy: rand(14, 28),
                rotation: rand(0, Math.PI * 2),
                spin: rand(-0.6, 0.6),
                flutter: rand(0, Math.PI * 2),
                flutterSpeed: rand(1.2, 2.4),
                size: 20 + depth * 28,
                image: Math.floor(rand(0, leafSources.length)),
                depth,
                roll: rand(0, Math.PI * 2),
                rollSpeed: rand(0.22, 0.42) * (Math.random() < 0.5 ? -1 : 1),
                pitch: rand(-0.2, 0.2),
            }
        }

        function sinePoint(r: Pick<WindRibbon, "originX" | "originY" | "dir" | "speed" | "waveK" | "waveK2" | "amp" | "amp2">, pathT: number) {
            const s = pathT * r.speed
            return {
                x: r.originX + r.dir * s,
                y: r.originY + Math.sin(pathT * r.waveK) * r.amp + Math.sin(pathT * r.waveK2 + 0.6) * r.amp2,
            }
        }

        function spawnRibbon() {
            const dir = targetWind >= 0 ? 1 : -1
            const originX = dir > 0 ? rand(-80, width * 0.15) : rand(width * 0.85, width + 80)
            let originY: number
            if (burstLanes.length > 0) {
                originY = burstLanes.shift() as number
            } else {
                originY = rand(height * 0.16, height * 0.82)
                const minGap = height * 0.22
                for (let attempt = 0; attempt < 10; attempt++) {
                    let tooClose = false
                    for (let i = 0; i < ribbons.length; i++) {
                        const live = ribbons[i]
                        if (live.t < 2.4 && Math.abs(live.originY - originY) < minGap) {
                            tooClose = true
                            break
                        }
                    }
                    if (!tooClose) {
                        break
                    }
                    originY = rand(height * 0.16, height * 0.82)
                }
            }
            const speed = rand(150, 245)
            const totalLife = 4
            const duration = rand(2.4, 2.7)
            const waveK = rand(1.4, 2.4)
            const waveK2 = waveK * 0.41
            const amp = rand(18, 42)
            const amp2 = amp * 0.28
            const hasLoop = Math.random() < 0.45
            const loopAt = hasLoop ? rand(duration * 0.22, duration * 0.42) : -1
            const loopRadius = rand(27.5, 50)
            const proto = { originX, originY, dir, speed, waveK, waveK2, amp, amp2 }
            const entry = loopAt >= 0 ? sinePoint(proto, loopAt) : { x: 0, y: 0 }
            const vx = dir * speed
            const vy = loopAt >= 0
                ? Math.cos(loopAt * waveK) * waveK * amp + Math.cos(loopAt * waveK2 + 0.6) * waveK2 * amp2
                : 0
            const mag = Math.hypot(vx, vy) || 1
            const tx = vx / mag
            const ty = vy / mag
            const startA = dir > 0 ? Math.atan2(tx, -ty) : Math.atan2(-tx, ty)
            const loopDur = loopAt >= 0 ? (Math.PI * 2 * loopRadius) / mag : 0
            ribbons.push({
                originX,
                originY,
                dir,
                speed,
                t: 0,
                duration,
                waveK,
                waveK2,
                amp,
                amp2,
                loopAt,
                loopDur,
                loopRadius,
                loopCenterX: entry.x - Math.cos(startA) * loopRadius,
                loopCenterY: entry.y - Math.sin(startA) * loopRadius,
                startA,
                fadeWindow: totalLife - duration,
                trail: [],
            })
        }

        function circlePoint(r: WindRibbon, u: number) {
            const a = r.startA - r.dir * u * Math.PI * 2
            return {
                x: r.loopCenterX + Math.cos(a) * r.loopRadius,
                y: r.loopCenterY + Math.sin(a) * r.loopRadius,
            }
        }

        function ribbonPoint(r: WindRibbon, t: number) {
            if (r.loopAt < 0) {
                return sinePoint(r, t)
            }
            const loopEnd = r.loopAt + r.loopDur
            if (t < r.loopAt) {
                return sinePoint(r, t)
            }
            if (t > loopEnd) {
                return sinePoint(r, t - r.loopDur)
            }
            return circlePoint(r, (t - r.loopAt) / r.loopDur)
        }

        function tick(now: number) {
            if (!running) {
                return
            }
            const dt = Math.min(0.033, (now - last) / 1000)
            last = now

            gustTimer -= dt
            if (gustTimer <= 0) {
                targetWind = rand(-38, 42)
                gustTimer = rand(2.2, 4.5)
                pendingSpawns += Math.random() < 0.4 ? 2 : 1
            }
            spawnWait -= dt
            if (pendingSpawns > 0 && spawnWait <= 0) {
                spawnRibbon()
                pendingSpawns -= 1
                spawnWait = burstSpawns ? rand(0.7, 1.15) : rand(1.1, 1.9)
                if (pendingSpawns === 0) {
                    burstSpawns = false
                }
            }
            wind += (targetWind - wind) * (1 - Math.exp(-dt * 2.4))
            const breeze = Math.sin(now / 2800) * 4
            const flow = wind * 0.35 + targetWind * 0.65 + breeze

            sortTick += 1
            if (sortTick % 12 === 0) {
                leaves.sort((a, b) => a.depth - b.depth)
            }
            for (const leaf of leaves) {
                const parallax = 0.22 + leaf.depth * 0.78
                leaf.flutter += leaf.flutterSpeed * dt
                const flutterX = Math.sin(leaf.flutter) * (14 + leaf.size * 0.25)
                const windForce = flow * (0.62 + leaf.depth * 1.35)
                leaf.vx += (windForce + flutterX * 0.08 - leaf.vx) * dt * 2.2
                leaf.vy += (22 * parallax - leaf.vy * 0.22) * dt
                leaf.vy = Math.min(Math.max(leaf.vy, 10), 42 * parallax)
                leaf.x += leaf.vx * dt * 4.4 * parallax
                leaf.y += leaf.vy * dt * 4.0 * parallax
                leaf.rotation += (leaf.spin + flow * 0.012 + Math.sin(leaf.flutter) * 0.8) * dt
                leaf.spin += (flow * 0.0024 - leaf.spin) * dt * 0.35
                const targetRoll = Math.sign(flow || leaf.rollSpeed || 1) * (0.26 + Math.abs(flow) * 0.0035)
                leaf.rollSpeed += (targetRoll - leaf.rollSpeed) * dt * 0.45
                leaf.roll += leaf.rollSpeed * dt
                leaf.pitch += (Math.sin(leaf.flutter * 0.45) * 0.28 + Math.sin(leaf.roll) * 0.1 - leaf.pitch) * dt * 1.8

                if (leaf.y > height + 50 || leaf.x < -90 || leaf.x > width + 90) {
                    Object.assign(leaf, spawnLeaf(false))
                }
            }

            for (let i = ribbons.length - 1; i >= 0; i--) {
                const r = ribbons[i]
                const prevT = r.t
                r.t += dt
                const inLoop = r.loopAt >= 0 && r.t > r.loopAt && prevT < r.loopAt + r.loopDur
                const steps = inLoop ? Math.max(1, Math.ceil(dt / 0.007)) : 1
                for (let s = 1; s <= steps; s++) {
                    r.trail.push(ribbonPoint(r, prevT + (r.t - prevT) * (s / steps)))
                }
                if (r.trail.length > 420) {
                    r.trail.splice(0, r.trail.length - 420)
                }
                if (r.t > r.duration + r.fadeWindow) {
                    ribbons.splice(i, 1)
                }
            }

            for (const leaf of leaves) {
                const parallax = 0.22 + leaf.depth * 0.78
                for (const r of ribbons) {
                    if (r.trail.length < 2) {
                        continue
                    }
                    const head = r.trail[r.trail.length - 1]
                    const prev = r.trail[r.trail.length - 2]
                    const dx = leaf.x - head.x
                    const dy = leaf.y - head.y
                    const dist = Math.hypot(dx, dy)
                    const radius = 110 + leaf.size * 1.4
                    if (dist > radius) {
                        continue
                    }
                    const influence = (1 - dist / radius) * (0.45 + leaf.depth * 0.7)
                    const rvx = (head.x - prev.x) / Math.max(dt, 0.001)
                    const rvy = (head.y - prev.y) / Math.max(dt, 0.001)
                    leaf.x += rvx * dt * influence
                    leaf.y += rvy * dt * influence * 0.7
                    leaf.vx += (rvx / (4.4 * parallax) - leaf.vx) * dt * influence * 1.6
                    leaf.rollSpeed += Math.sign(rvx || 1) * 0.18 * influence * dt
                    leaf.pitch += rvy * 0.0008 * influence
                }
            }

            ctx!.clearRect(0, 0, width, height)

            for (const leaf of leaves) {
                const img = images[leaf.image]
                if (!img) {
                    continue
                }
                ctx!.save()
                ctx!.translate(leaf.x, leaf.y)
                ctx!.rotate(leaf.rotation)
                const rollCos = Math.cos(leaf.roll)
                const pitchCos = Math.cos(leaf.pitch)
                ctx!.transform(1, Math.sin(leaf.roll) * 0.08, Math.sin(leaf.pitch) * 0.1, 1, 0, 0)
                ctx!.scale(rollCos, Math.max(0.78, Math.abs(pitchCos)))
                const wobble = 1 + Math.sin(leaf.flutter) * 0.06
                const w = leaf.size * wobble
                const h = (img.height / img.width) * w
                const facing = 0.82 + 0.18 * Math.abs(rollCos)
                ctx!.globalAlpha = (0.28 + leaf.depth * 0.62) * facing
                ctx!.drawImage(img, -w / 2, -h / 2, w, h)
                ctx!.restore()
            }

            ctx!.lineCap = "round"
            ctx!.lineJoin = "round"
            ctx!.strokeStyle = "#fff8e4"
            for (const r of ribbons) {
                if (r.trail.length < 2) {
                    continue
                }
                const lifeFade = r.t <= r.duration
                    ? 1
                    : Math.max(0, 1 - (r.t - r.duration) / r.fadeWindow)
                if (lifeFade <= 0.01) {
                    continue
                }
                const n = r.trail.length - 1
                const step = Math.max(1, Math.floor(n / 72))
                ctx!.globalAlpha = 0.25 * lifeFade
                for (let i = step; i <= n; i += step) {
                    const prev = r.trail[i - step]
                    const cur = r.trail[Math.min(i, n)]
                    const u = i / n
                    ctx!.beginPath()
                    ctx!.lineWidth = 0.5 + Math.sin(u * Math.PI) * 6.2
                    ctx!.moveTo(prev.x, prev.y)
                    ctx!.lineTo(cur.x, cur.y)
                    ctx!.stroke()
                }
            }
            ctx!.globalAlpha = 1

            raf = requestAnimationFrame(tick)
        }

        function triggerCardFlipBurst(e: Event) {
            if ((e as CustomEvent).detail?.showingFront === false) {
                return
            }
            const dir = Math.random() < 0.5 ? -1 : 1
            targetWind = dir * rand(78, 118)
            wind = targetWind
            burstSpawns = true
            pendingSpawns = 3
            spawnWait = rand(0.35, 0.55)
            gustTimer = rand(4.2, 5.8)
            const lanes = [0.2, 0.5, 0.8]
            for (let i = lanes.length - 1; i > 0; i--) {
                const j = Math.floor(Math.random() * (i + 1))
                const tmp = lanes[i]
                lanes[i] = lanes[j]
                lanes[j] = tmp
            }
            burstLanes = lanes.map(u => height * (u + rand(-0.05, 0.05)))
            for (const leaf of leaves) {
                const kick = rand(22, 48) * (0.45 + leaf.depth * 0.8)
                leaf.vx += dir * kick
                leaf.spin += dir * rand(0.5, 1.4)
                leaf.rollSpeed += dir * rand(0.35, 0.8)
            }
        }

        resize()
        window.addEventListener("resize", resize)
        window.addEventListener(CARDS_FLIPPED_EVENT, triggerCardFlipBurst)

        loadImages().then(loaded => {
            if (!running) {
                return
            }
            images = loaded
            const count = Math.round(Math.min(28, Math.max(16, Math.min(width, 1920) / 72)))
            for (let i = 0; i < count; i++) {
                leaves.push(spawnLeaf(true))
            }
            last = performance.now()
            raf = requestAnimationFrame(tick)
        }).catch(() => undefined)

        return () => {
            running = false
            cancelAnimationFrame(raf)
            window.removeEventListener("resize", resize)
            window.removeEventListener(CARDS_FLIPPED_EVENT, triggerCardFlipBurst)
        }
    }, [])

    return (
        <canvas
            ref={canvasRef}
            className="falling-leaves-canvas"
            aria-hidden
        />
    )
}
